'use client'

import { calculateDistance } from '@/hooks'
import { cn } from '@/lib/utils'
import { useStoreAccomodation, useStoreGeo, useStoreI18n } from '@/store'
import {
  Typo3AccomodationItem,
  Typo3CultureItem,
  Typo3EatDrinkItem,
  Typo3FreetimeItem,
  Typo3Image,
  Typo3NatureItem,
  Typo3SectionSpotSanityStoryList_Item,
  Typo3ShoppingItem,
  Typo3SportItem,
  Typo3SportServicesItem,
  Typo3TourItem
} from '@/types'
import { Typo3EventItem } from '@/types/typo3/sections/spotsanity-eventlist'
import { DirectoryTeaser } from '@/types/typo3/sections/spotsanity-story/detail'
import { generateAltText } from '@/utils'
import { MEDIA } from '@spot'
import React, { memo, useCallback, useMemo } from 'react'
import Title from '../Base/Title'
import Button from '../Button/Button'
import Img from '../Img/Img'
import OpeningStatus from '../OpeningStatus/OpeningStatus'
import RatingStars, { RatingStarsProps } from '../RatingStars/RatingStars'

type DataType =
  | Typo3EventItem
  | Typo3AccomodationItem
  | Typo3EatDrinkItem
  | Typo3NatureItem
  | Typo3ShoppingItem
  | Typo3CultureItem
  | Typo3FreetimeItem
  | Typo3SportItem
  | Typo3TourItem
  | Typo3SportServicesItem
  | DirectoryTeaser

interface Cta5050Props extends Omit<React.HTMLProps<HTMLDivElement>, 'data'> {
  variant: 'hotel' | 'event' | 'tour' | 'story' | 'sanityPlugin' | 'default'
  overline?: string
  title?: string
  subtitle?: string
  description?: string
  image?: Typo3Image
  imagePosition?: 'left' | 'right'
  button?: React.ReactNode
  data?: DataType
  dataStory?: Typo3SectionSpotSanityStoryList_Item
}

const Cta5050 = ({
  overline,
  variant = 'default',
  imagePosition = 'left',
  image,
  description,
  subtitle,
  title,
  button,
  data,
  dataStory,
  className,
  ...props
}: Cta5050Props) => {
  //stores
  const D = useStoreI18n.use.dictionary()
  const currentLocale = useStoreI18n.use.currentLocale()
  const currentLocation = useStoreGeo.use.location()
  const roomsData = useStoreAccomodation.use.roomsData()

  //Composers
  const gridClass = cn(
    'relative flex md:grid md:grid-cols-1',
    variant == 'story' &&
      'bg-primary-300 text-neutral-100! [&_.overline]:text-neutral-100 [&_.title]:text-neutral-100!',
    className
  )
  const imageClass = cn(
    'w-1/2 border border-neutral-300 p-md md:w-full',
    imagePosition === 'right' && 'order-2'
  )
  const contentClass = cn(
    'gap-y-8 relative col-span-1 flex w-1/2 flex-col items-start justify-center border border-neutral-300 border-l-0 p-lg md:w-full md:border-t-0 md:border-r md:border-l md:px-md md:pl-md md:pr-md',
    imagePosition === 'right' &&
      'order-1 border-l border-neutral-300 border-r-0 md:border-t md:border-b-0 md:border-r'
  )

  const getOverline = (): string | undefined => {
    if (data) {
      switch (variant) {
        case 'event':
          if (
            'textDates' in data &&
            data?.textDates &&
            !!data.textDates.length
          ) {
            return data.textDates[0].firstActive
          }
          break

        case 'sanityPlugin':
          if ('type' in data && data?.category) {
            return data.category
          }
          break

        default:
          return overline
      }
    } else if (dataStory) {
      return dataStory?.category
    }
    return overline
  }

  const getTitle = useCallback((): string | undefined => {
    if (data && 'title' in data && data.title) {
      return data.title
    } else if (dataStory) {
      return dataStory?.contentTeaser?.title
    } else {
      return title
    }
  }, [data, dataStory, title])

  const getSubTitle = (): string | undefined => {
    if (data) {
      switch (variant) {
        case 'sanityPlugin':
          if ('contact' in data && data?.contact) {
            return data.contact.city
          }
          break

        default:
          return subtitle
      }
    } else if (dataStory) {
      return undefined
    }
    return subtitle
  }

  const getDescription = useCallback((): string | undefined => {
    if (data) {
      switch (variant) {
        case 'default':
          return description
        default:
          return data && 'summary' in data ? data.summary : ''
      }
    } else if (dataStory) {
      return dataStory?.contentTeaser?.summary
    }
    return description
  }, [data, dataStory, variant, description])

  const getButton = (): React.ReactNode | undefined => {
    if (data && 'slug' in data && data?.slug) {
      return (
        <Button
          variant="primary"
          icon="material-symbols:chevron-right"
          href={data?.slug}
          nextLink
          isTransition
        >
          {D['fe.content.learnmore']}
        </Button>
      )
    } else if (variant === 'tour' && data && 'link' in data && data?.link) {
      return (
        <Button
          variant="primary"
          icon="material-symbols:chevron-right"
          href={data?.link?.href}
          nextLink={data?.link?.target === '_blank' ? false : true}
          isTransition={data?.link?.target === '_blank' ? false : true}
          target={data?.link?.target || undefined}
        >
          {data?.link?.title}
        </Button>
      )
    } else if (variant === 'sanityPlugin' && data && 'uri' in data) {
      return (
        <Button
          variant="primary"
          icon="material-symbols:chevron-right"
          href={data?.uri?.href}
          nextLink
          isTransition
          // className={$.button_story}
        >
          {data?.uri?.title ? data?.uri?.title : D['fe.content.learnmore']}
        </Button>
      )
    } else if (dataStory) {
      return (
        <Button
          variant="primary"
          icon="material-symbols:chevron-right"
          href={dataStory?.slug}
          nextLink
          isTransition
          // className={cn(variant === 'story' && $.button_story)}
        >
          {D['fe.content.learnmore']}
        </Button>
      )
    } else {
      return button
    }
  }

  const getBadge = (): React.ReactNode | undefined => {
    if (data) {
      switch (variant) {
        case 'event':
          if ('isTopEvent' in data && data?.isTopEvent) {
            return (
              <div className="gap-x-xs bg-primary-300 p-xs px-sm relative top-0 right-0 flex items-center justify-start text-neutral-100! max-sm:relative max-sm:justify-self-start">
                <p className="text-btn-small">{D['fe.content.topevent']}</p>
              </div>
            )
          }
          break

        // case 'tour':
        //   return (
        //     <TourBadge
        //       data={data as Typo3TourItem}
        //       className={cn(
        //         'border border-neutral-300 md:relative',
        //         imagePosition === 'right' && 'right-auto left-0'
        //       )}
        //     />
        //   )

        default:
          if ('openings' in data) {
            return (
              <OpeningStatus
                data={data?.openings}
                className={cn(
                  'border border-neutral-300 md:relative',
                  imagePosition === 'right' && 'right-auto left-0'
                )}
              />
            )
          }
      }
    }

    return undefined
  }

  const imageAlt = useMemo(() => {
    if (!image) {
      return ''
    }

    const imageTitle = getTitle()
    const imageDescription = getDescription()

    switch (variant) {
      case 'event':
        return generateAltText(
          'event',
          imageTitle,
          imageDescription,
          currentLocale
        )
      case 'hotel':
        return generateAltText(
          'accommodation',
          imageTitle,
          imageDescription,
          currentLocale
        )
      case 'story':
        return generateAltText(
          'story',
          imageTitle,
          imageDescription,
          currentLocale
        )
      default:
        return imageTitle || ''
    }
  }, [variant, image, currentLocale, getTitle, getDescription])

  return (
    <div className={gridClass} {...props}>
      {image && (
        <div className={imageClass}>
          <Img
            crop={{ width: 700, height: 700 }}
            data={image}
            alt={imageAlt}
            className="w-full pt-[100%] max-sm:pt-[100%] md:pt-[66%]"
            objectFit
            fill
            quality={90}
            sizes={`${MEDIA.tablet} 100vw, 60rem`}
          />
        </div>
      )}
      <div className={contentClass}>
        {getBadge() && getBadge()}
        {getOverline() && (
          <div className="mb-sm text-neutral-400 overline">{getOverline()}</div>
        )}
        {getTitle() && (
          <Title as="h2" className="title">
            {getTitle()}
          </Title>
        )}

        {getSubTitle() && <p className="text-large">{getSubTitle()}</p>}
        <div>
          {data && variant === 'hotel' && 'city' in data && data?.city && (
            <p>{data.city}</p>
          )}
          {getDescription() && <p>{getDescription()}</p>}
        </div>
        <div className="gap-x-xs flex items-center">
          {/* {variant === 'tour' && <TourStatus data={data} />} */}
          {variant === 'hotel' &&
            data &&
            'stars' in data &&
            data?.stars &&
            data.stars !== 'stars-0' && (
              <>
                <RatingStars
                  stars={
                    data?.isSuperior
                      ? 6
                      : (data.stars.replace(
                          'stars-',
                          ''
                        ) as RatingStarsProps['stars'])
                  }
                />
              </>
            )}
          {variant === 'hotel' &&
            data &&
            'stcId' in data &&
            roomsData &&
            !!roomsData.length &&
            roomsData
              .filter((room) => room.stc_id === data.stcId)
              .map((room, index) => (
                <>
                  {data.stars && data.stars !== 'stars-0' && <div>·</div>}
                  <div
                    className="font-weight-700"
                    key={index?.toString()}
                  >{`${D['fe.booking.priceFrom']} CHF ${room?.price?.toFixed(2)}`}</div>
                </>
              ))}
          {data &&
            variant !== 'default' &&
            variant !== 'tour' &&
            'coordinates' in data &&
            data.coordinates?.hasCoordinates &&
            data.coordinates.latitude &&
            data.coordinates.longitude &&
            currentLocation.latitude !== 0 &&
            currentLocation.longitude !== 0 && (
              <>
                {(variant === 'hotel' && roomsData && !!roomsData.length) ||
                  ('stars' in data &&
                    data.stars &&
                    data.stars !== 'stars-0' && <div>·</div>)}
                <div>
                  {`${calculateDistance(
                    {
                      latitude: currentLocation.latitude,
                      longitude: currentLocation.longitude
                    },
                    {
                      latitude: +data.coordinates.latitude,
                      longitude: +data.coordinates.longitude
                    }
                  ).toFixed(1)} km`}
                </div>
              </>
            )}
        </div>
        {getButton() && <div>{getButton()}</div>}
      </div>
    </div>
  )
}

export default memo(Cta5050)
